"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import idTranslations, { Translations } from "./translations/id";
import enTranslations from "./translations/en";

// ===========================================
// TYPES
// ===========================================

export type Language = "id" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: Translations;
}

// ===========================================
// TRANSLATIONS MAP
// ===========================================

const translationsMap: Record<Language, Translations> = {
  id: idTranslations,
  en: enTranslations,
};

// ===========================================
// CONTEXT
// ===========================================

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// ===========================================
// STORAGE KEY
// ===========================================

const STORAGE_KEY = "kinken-language";

// ===========================================
// PROVIDER
// ===========================================

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}


export function LanguageProvider({
  children,
  defaultLanguage = "id",
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage);
  const [mounted, setMounted] = useState(false);
  const router = useRouter(); // Create hook below or import from next/navigation
  const pathname = usePathname();

  // Sync state with URL prop (defaultLanguage)
  useEffect(() => {
    setLanguageState(defaultLanguage);
    localStorage.setItem(STORAGE_KEY, defaultLanguage);
  }, [defaultLanguage]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Set language and redirect
  const setLanguage = (lang: Language) => {
    if (lang === language) return;
    
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    
    // Set cookie for middleware persistence
    // Expires in 1 year, path /
    document.cookie = `NEXT_LOCALE=${lang}; path=/; max-age=31536000; SameSite=Lax`;
    
    // Robust Redirect logic using window.location (Source of Truth)
    // We use window.location.pathname to ensure we work with the ACTUAL browser URL,
    // bypassing potential Next.js usePathname normalization on 404 pages.
    const currentPath = window.location.pathname;
    const segments = currentPath.split('/').filter(Boolean);
    const firstSegment = segments[0];

    if (firstSegment === 'id' || firstSegment === 'en') {
        segments[0] = lang; // Replace existing locale
    } else {
        segments.unshift(lang); // Prepend locale if missing
    }

    const newPath = '/' + segments.join('/');
    window.location.href = newPath;
  };

  // Get translations for current language
  const translations = translationsMap[language];

  // Prevent hydration mismatch by using default translations until mounted
  const currentTranslations = mounted ? translations : translationsMap[defaultLanguage];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translations: currentTranslations }}>
      {children}
    </LanguageContext.Provider>
  );
}

// ===========================================
// HOOKS
// ===========================================

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

/**
 * Hook to get the full translations object for current language
 * Usage: const t = useTranslations(); console.log(t.rooms.title);
 */
export function useTranslations(): Translations {
  const { translations } = useLanguage();
  return translations;
}

/**
 * Legacy hook for compatibility
 */
export function useTranslation() {
  const { translations, language } = useLanguage();
  return { t: translations, language };
}
